import { z } from 'zod'

const optionalString = z.preprocess(
	value => (value === '' ? undefined : value),
	z.string().trim().min(1).optional(),
)

const optionalUrl = z.preprocess(
	value => (value === '' ? undefined : value),
	z.string().url().optional(),
)

const mediaOrigin = z.string().url().refine(value => {
	try {
		const url = new URL(value)
		return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password && url.pathname === '/' && !url.search && !url.hash
	} catch { return false }
}, 'VITE_MEDIA_API_URL must be an HTTP(S) origin')

const schema = z.object({
	VITE_MEDIA_API_URL: mediaOrigin.default('http://localhost:18081'),
	VITE_ADMIN_API_URL: z.string().url().default('http://localhost:18090'),
	VITE_AUTH_ENABLED: z.enum(['true', 'false']).default('false'),
	VITE_KEYCLOAK_URL: optionalUrl,
	VITE_KEYCLOAK_REALM: optionalString,
	VITE_KEYCLOAK_CLIENT_ID: z.string().trim().min(1).default('tisit-admin-web'),
	VITE_KEYCLOAK_ROLES_CLIENT_ID: z.preprocess(value => value === '' ? undefined : value, z.string().min(1).default('fitness-admin-api')),
})

export const parseApplicationEnv = (input: unknown, { release = false } = {}) => {
	const result = schema.safeParse(input)
	if (!result.success) {
		throw new Error(`Invalid application environment: ${result.error.message}`)
	}
	const values = result.data
	const authEnabled = values.VITE_AUTH_ENABLED === 'true'
	if (release) {
		const explicit = input as Record<string, unknown>
		for (const name of ['VITE_ADMIN_API_URL', 'VITE_MEDIA_API_URL', 'VITE_AUTH_ENABLED', 'VITE_KEYCLOAK_URL', 'VITE_KEYCLOAK_REALM', 'VITE_KEYCLOAK_CLIENT_ID']) {
			const value = explicit[name]
			if (typeof value !== 'string' || !value.trim()) throw new Error(`${name} must be explicit in runtime configuration`)
		}
		if (!authEnabled) throw new Error('Authentication must be enabled in runtime configuration')
	}
	if (authEnabled && (!values.VITE_KEYCLOAK_URL || !values.VITE_KEYCLOAK_REALM)) {
		throw new Error(
			'VITE_KEYCLOAK_URL and VITE_KEYCLOAK_REALM are required when authentication is enabled',
		)
	}
	return {
		mediaApiUrl: new URL(values.VITE_MEDIA_API_URL).origin,
		adminApiUrl: values.VITE_ADMIN_API_URL.replace(/\/$/, ''),
		authEnabled,
		keycloakUrl: values.VITE_KEYCLOAK_URL,
		keycloakRealm: values.VITE_KEYCLOAK_REALM,
		keycloakClientId: values.VITE_KEYCLOAK_CLIENT_ID,
		keycloakRolesClientId: values.VITE_KEYCLOAK_ROLES_CLIENT_ID,
	}
}
